import React from 'react';
import {
  Box,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Grid,
  IconButton,
  Typography,
} from '@mui/material';
import { SparkButton } from 'sharedComponents/UI';
import { useCurrentUser, useResize } from 'hooks/common';
import { Close } from '@mui/icons-material';
import { useWorkspaceRecommendations } from 'hooks/Workspace/useWorkspaceRecommendations';
import { useNavigate } from 'react-router-dom';
import { useUpdateWorkSpaceRecommendation } from 'hooks/Workspace/useUpdateWorkSpaceRecommendations';
import { useWorkspaceStore } from 'stores';
import DashboardTopSkeleton from 'sharedComponents/Skeleton/DashboardTopKeleton';

function DashboardTopSection() {
  const currentWorkspaceID = useWorkspaceStore(
    React.useCallback((state) => state.currentWorkspaceID, [])
  );
  const { recommendations, isLoading } =
    useWorkspaceRecommendations(currentWorkspaceID);

  const { applicationUser } = useCurrentUser();

  const { mutate: updateRecommendation } = useUpdateWorkSpaceRecommendation();
  const navigate = useNavigate();
  const { isMobile } = useResize();

  const handleClick = (id: string) => {
    updateRecommendation({ id, dismissedAt: new Date().toISOString() });
  };

  if (isLoading) {
    return <DashboardTopSkeleton />;
  }

  return (
    <Grid item container gap={2}>
      <Grid
        container
        alignItems='center'
        position='relative'
        marginTop={isMobile ? '160px' : 'auto'}
      >
        <Box
          display={'flex'}
          gap={0.5}
          justifyContent='center'
          paddingLeft={2}
          flexDirection='column'
        >
          <Box display={'flex'} gap={0.5}>
            <Typography
              variant='h3'
              className='color-text'
              sx={{
                fontFamily: 'Mont-SemiBold',
              }}
            >
              {`Hello ${applicationUser?.attributes?.name || ''}`}
            </Typography>
            <Typography sx={{ fontFamily: 'Mont-SemiBold', fontSize: '38px' }}>
              👋
            </Typography>
          </Box>

          <Typography
            variant='h5'
            sx={{
              fontFamily: 'Mont-semiBold',
              color: '#404040',
            }}
          >
            How can I help you today ?
          </Typography>
        </Box>
      </Grid>

      <Grid container spacing={2} justifyContent='center' paddingLeft={2}>
        {recommendations?.map((recommendation, index) => {
          const canDismiss = recommendation.meta?.user?.permissions?.canDismiss;
          return (
            <Grid item key={index} xs={12} sm={6} md={4} lg={4}>
              <Card
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  cursor: 'pointer',
                  borderRadius: '12px',
                  transition: 'transform 0.1s',
                  width: '100%',
                  aspectRatio: '2.15 / 1',
                  background: '#ECEFFE',
                  borderColor: '#6267AC',
                  minHeight: '150px',
                  minWidth: '200px',
                  '&:hover': {
                    boxShadow: (theme) => theme.shadows[4],
                  },
                  '&:active': {
                    transform: 'scale(0.98)',
                  },
                  borderWidth: '1px',
                  borderStyle: 'solid',
                }}
                elevation={0}
              >
                <Box display='flex'>
                  <CardContent
                    sx={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    <Typography
                      variant='body1'
                      sx={{
                        display: '-webkit-box',
                        color: '#4A4A4A',
                        fontStyle: 'normal',
                        lineHeight: 'normal',
                        overflow: 'hidden',
                        WebkitBoxOrient: 'vertical',
                        WebkitLineClamp: 3,
                        fontSize: {
                          xs: '14px',
                          sm: '16px',
                        },
                      }}
                    >
                      {recommendation.attributes.recommendation}
                    </Typography>
                  </CardContent>
                  {!canDismiss && (
                    <CardHeader
                      action={
                        <IconButton
                          onClick={() => handleClick(recommendation.id)}
                        >
                          <Close />
                        </IconButton>
                      }
                    />
                  )}
                </Box>
                <CardActions
                  disableSpacing
                  sx={{
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    paddingLeft: '12px',
                    paddingBottom: '12px',
                    mt: 'auto',
                  }}
                >
                  <SparkButton
                    variant='contained'
                    onClick={() => navigate(recommendation.attributes.ctaLink)}
                    sx={{
                      borderRadius: '2px',
                      fontSize: {
                        xs: '12px',
                        sm: '14px',
                      },
                      fontStyle: 'normal',
                      lineHeight: 'normal',
                      width: {
                        xs: '100%',
                        sm: 'auto',
                      },
                      whiteSpace: 'nowrap',
                      height: '40px',
                      '&:hover': {
                        background: '#6267AC',
                      },
                    }}
                  >
                    {recommendation.attributes.ctaLabel}
                  </SparkButton>
                </CardActions>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Grid>
  );
}

export default DashboardTopSection;
